/**
 * M7 S30: the story sites (plan S30; spec §5–§7). A deterministic overlay on the M3 layout, drawn from the galaxy
 * seed; the M3 generator is untouched (`buildSystemLayout` runs as before and the overlay only reads its output).
 *
 * - **Column Nine** (Mission 1's field of hulls in queue order): a system of the configured region (deep), not home,
 *   preferring one with no anchor; the field sits outside a body's shell, clear of stations, relays, lane rings and
 *   POIs.
 * - **Okafor's lab** (the derelict): an existing derelict POI of the `okafor-hideout` anchor system when there is
 *   one, else a placed site there.
 * - **Story ship spawns** (`spawnStoryShip`): a point per ship key in its system, same clearance rules.
 * - **System hints:** `resolveSystemHint` turns a story hint (`column-nine`, `okafor-lab`, `harrow`, ...) or an M3
 *   anchor (`okafor-hideout`, the S27 GAP) into a worker system id.
 *
 * Event-driven (system entry, a mission step), never per frame; it may allocate. Tuning in `story/sites.json`.
 */
import { z } from 'zod'
import sitesJson from '../data/story/sites.json'
import type { AnchorId, Galaxy, RegionId, StarSystem } from '../galaxy/types'
import type { SystemLayout, Vec3T } from '../galaxy/layout/types'

const rangeSchema = z.tuple([z.number().nonnegative(), z.number().positive()]).refine(([a, b]) => a <= b, 'min > max')

export const sitesConfigSchema = z.object({
  comment: z.string().optional(),
  clearanceM: z.number().positive(),
  tries: z.number().int().min(1),
  offsetM: rangeSchema,
  /** The most a placed site sits above or below the disc plane, as a share of its distance from the body (ST-1). */
  planeHeight: z.number().min(0).max(1),
  columnNine: z.object({ region: z.enum(['gate', 'mid', 'deep', 'rim']), radiusM: z.number().positive() }),
  okaforLab: z.object({ anchor: z.enum(['home-gate', 'authority-seat', 'megacorp-hq', 'descendant-capital', 'guardian-base', 'okafor-hideout']) }),
})
export type SitesConfig = z.infer<typeof sitesConfigSchema>

let cached: SitesConfig | null = null
export function sitesConfig(): SitesConfig {
  cached ??= sitesConfigSchema.parse(sitesJson)
  return cached
}

/** One placed point of the overlay. */
export interface StorySite {
  readonly systemId: string
  readonly position: Vec3T
  readonly radiusM: number
  /** The M3 POI it reuses (Okafor's lab on an existing derelict), else a story id. */
  readonly poiId: string
}

export interface StorySites {
  readonly seed: number
  readonly columnNine: StorySite
  readonly okaforLab: StorySite
}

// ---------------------------------------------------------------------------------------------
// Deterministic draws
// ---------------------------------------------------------------------------------------------

/** FNV-1a of a key, mixed with the galaxy seed. */
export function siteHash(seed: number, key: string): number {
  let h = (0x811c9dc5 ^ (seed >>> 0)) >>> 0
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h
}

/** mulberry32 over a hashed key: the same seed and key always give the same stream. */
function stream(seed: number, key: string): () => number {
  let a = siteHash(seed, key)
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function dist(a: Vec3T, b: Vec3T): number {
  const dx = a[0] - b[0]
  const dy = a[1] - b[1]
  const dz = a[2] - b[2]
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

/** The nearest distance from `p` to a station, relay, lane ring or POI (minus nothing), and whether it clears bodies. */
export function siteClearance(layout: SystemLayout, p: Vec3T, ignorePoi: string | null = null): { structuresM: number; outsideBodies: boolean } {
  let min = Infinity
  for (const s of layout.stations) min = Math.min(min, dist(p, s.position))
  for (const r of layout.relays) min = Math.min(min, dist(p, r.position))
  for (const r of layout.laneRings) min = Math.min(min, dist(p, r.position))
  for (const poi of layout.pois) if (poi.id !== ignorePoi) min = Math.min(min, dist(p, poi.position))
  let outside = true
  for (const b of layout.bodies) if (dist(p, b.position) < b.shellM) outside = false
  return { structuresM: min, outsideBodies: outside }
}

/**
 * A point outside a seeded body's shell (`offsetM` beyond it), clear of every structure by `clearanceM + radiusM`.
 * Falls back to the best-clearance candidate when none clears (never seen on the test seeds; logged by the tests).
 */
export function placeSite(layout: SystemLayout, seed: number, key: string, radiusM: number, cfg: SitesConfig = sitesConfig()): Vec3T {
  const rnd = stream(seed, `${layout.systemId}/${key}`)
  const need = cfg.clearanceM + radiusM
  let best: Vec3T = [0, 0, 0]
  let bestScore = -Infinity
  for (let i = 0; i < cfg.tries; i++) {
    const body = layout.bodies.length > 0 ? layout.bodies[Math.floor(rnd() * layout.bodies.length)] : undefined
    const origin: Vec3T = body?.position ?? [0, 0, 0]
    const base = (body?.shellM ?? layout.star.radiusM * 3) + radiusM
    const r = base + cfg.offsetM[0] + rnd() * (cfg.offsetM[1] - cfg.offsetM[0])
    const theta = rnd() * Math.PI * 2
    // In the disc plane (the world x–z, y up; galaxy/layout/frame.ts) with a small height off it, like the hulks.
    // ST-1: this was the x–y plane, which put sites above and below their body instead of beside it.
    const yf = (rnd() - 0.5) * 2 * cfg.planeHeight
    const xz = Math.sqrt(1 - yf * yf)
    const p: Vec3T = [origin[0] + r * xz * Math.cos(theta), origin[1] + r * yf, origin[2] + r * xz * Math.sin(theta)]
    const c = siteClearance(layout, p)
    if (c.outsideBodies && c.structuresM >= need) return p
    const score = (c.outsideBodies ? 0 : -1e18) + Math.min(c.structuresM, 1e18)
    if (score > bestScore) {
      bestScore = score
      best = p
    }
  }
  return best
}

// ---------------------------------------------------------------------------------------------
// The overlay
// ---------------------------------------------------------------------------------------------

/** The system of an M3 anchor, or undefined. */
export function anchorSystem(galaxy: Galaxy, anchor: AnchorId): StarSystem | undefined {
  return galaxy.systems.find((s) => s.anchor === anchor)
}

/** Column Nine's system: the region's systems (anchor-free first), drawn by the seed; never home. */
export function columnNineSystem(galaxy: Galaxy, region: RegionId = sitesConfig().columnNine.region): StarSystem {
  const notHome = galaxy.systems.filter((s) => s.id !== galaxy.homeSystemId)
  const inRegion = notHome.filter((s) => s.region === region)
  const free = inRegion.filter((s) => s.anchor === null)
  const pool = free.length > 0 ? free : inRegion.length > 0 ? inRegion : notHome.length > 0 ? notHome : galaxy.systems
  return pool[siteHash(galaxy.seed, 'column-nine') % pool.length]
}

/** The overlay for a galaxy (`layoutOf` is the Reach core's cached layout). */
export function storySites(galaxy: Galaxy, layoutOf: (systemId: string) => SystemLayout, cfg: SitesConfig = sitesConfig()): StorySites {
  const c9 = columnNineSystem(galaxy, cfg.columnNine.region)
  const c9Layout = layoutOf(c9.id)
  const columnNine: StorySite = {
    systemId: c9.id,
    position: placeSite(c9Layout, galaxy.seed, 'column-nine', cfg.columnNine.radiusM, cfg),
    radiusM: cfg.columnNine.radiusM,
    poiId: `${c9.id}-column-nine`,
  }
  const labSystem = anchorSystem(galaxy, cfg.okaforLab.anchor) ?? c9
  const labLayout = layoutOf(labSystem.id)
  const derelicts = labLayout.pois.filter((p) => p.kind === 'derelict')
  const derelict = derelicts.length > 0 ? derelicts[siteHash(galaxy.seed, 'okafor-lab') % derelicts.length] : undefined
  const okaforLab: StorySite =
    derelict !== undefined
      ? { systemId: labSystem.id, position: derelict.position, radiusM: 0, poiId: derelict.id }
      : { systemId: labSystem.id, position: placeSite(labLayout, galaxy.seed, 'okafor-lab', 0, cfg), radiusM: 0, poiId: `${labSystem.id}-okafor-lab` }
  return { seed: galaxy.seed, columnNine, okaforLab }
}

/** Where a story ship (`spawnStoryShip.key`) appears in its system. */
export function storyShipSpawn(galaxy: Galaxy, layout: SystemLayout, key: string, cfg: SitesConfig = sitesConfig()): Vec3T {
  return placeSite(layout, galaxy.seed, `ship:${key}`, 0, cfg)
}

const ANCHORS: ReadonlySet<string> = new Set<AnchorId>(['home-gate', 'authority-seat', 'megacorp-hq', 'descendant-capital', 'guardian-base', 'okafor-hideout'])

/**
 * A story system hint (lead `systemHint`s, `m3m4.json` systems) as a worker system id: a system id stays; an M3
 * anchor becomes its system; `column-nine` and `okafor-lab` their sites' systems; `harrow` and `home` the home
 * system. Null for an unknown hint (`convoy-wrecks`, `fr-colony` and the like resolve when their content lands).
 */
export function resolveSystemHint(galaxy: Galaxy, sites: StorySites, hint: string): string | null {
  if (galaxy.systems.some((s) => s.id === hint)) return hint
  if (ANCHORS.has(hint)) return anchorSystem(galaxy, hint as AnchorId)?.id ?? null
  switch (hint) {
    case 'column-nine':
      return sites.columnNine.systemId
    case 'okafor-lab':
      return sites.okaforLab.systemId
    case 'harrow':
    case 'home':
      return galaxy.homeSystemId
    default:
      return null
  }
}
